import { useQueryClient } from '@zg-rentals/react-query5';

import {
  useRentCreditReportEnrollRenterInCreditTrackingHandler_enrollRenterInCreditTracking,
  useRentCreditReportGetOptInWithCreditTrackingHandler_getOptInWithCreditTracking,
  useRentCreditReportGetPaymentReportHistoryHandler_getPaymentReportHistory,
  useRentCreditReportOptInHandler_optIn,
  useRentCreditReportOptOutHandler_optOut,
  useRentCreditReportUnenrollRenterFromCreditTrackingHandler_unenrollRenterFromCreditTracking,
} from '@zg-rentals/ts-rent-payment-client-react-query';

import type {
  RentCreditReportGetOptInWithCreditTrackingWebApiResponseDto,
  RentCreditReportOptInWebApiRequestDto,
  RentCreditReportWebApiPaymentReportHistoryResponseDto,
} from '@zg-rentals/ts-rent-payment-client';

// Re-export the response types for consumers
export type {
  RentCreditReportGetOptInWithCreditTrackingWebApiResponseDto,
  RentCreditReportWebApiPaymentReportHistoryResponseDto,
};

/**
 * Hook to fetch payment reporting status with credit tracking info
 * Replaces: requestPaymentReportingStatusWithCreditTracking (RSAA)
 */
export function usePaymentReportingStatusWithCreditTracking(
  rentTermsKey: string | undefined,
  options?: { enabled?: boolean },
) {
  return useRentCreditReportGetOptInWithCreditTrackingHandler_getOptInWithCreditTracking(
    { rentTermsKey: rentTermsKey ?? '' },
    {
      enabled: Boolean(rentTermsKey) && (options?.enabled ?? true),
    },
  );
}

/**
 * Hook to fetch payment report history
 * Replaces: requestPaymentReportingHistory (RSAA)
 */
export function usePaymentReportingHistory(rentTermsKey: string | undefined, options?: { enabled?: boolean }) {
  return useRentCreditReportGetPaymentReportHistoryHandler_getPaymentReportHistory(
    { rentTermsKey: rentTermsKey ?? '' },
    {
      enabled: Boolean(rentTermsKey) && (options?.enabled ?? true),
    },
  );
}

/**
 * Hook to get a refetch function for credit tracking status
 * This can be used to manually refresh the data (similar to fetchCreditTrackingStatus in the container)
 */
export function useRefetchCreditTrackingStatus() {
  const queryClient = useQueryClient();

  return (rentTermsKey: string) => {
    return queryClient.invalidateQueries({
      queryKey: useRentCreditReportGetOptInWithCreditTrackingHandler_getOptInWithCreditTracking.getKey({
        rentTermsKey,
      }),
    });
  };
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Hook to opt out of payment reporting
 * Replaces: requestPaymentReportingOptOut (RSAA)
 *
 * @example
 * const optOutMutation = usePaymentReportingOptOut({
 *   onSuccess: () => showNotification(PAYMENT_REPORTING_OPT_OUT_SUCCESS),
 * });
 * await optOutMutation.mutateAsync({ rentTermsKey });
 */
export function usePaymentReportingOptOut(options?: {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: unknown) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();

  return useRentCreditReportOptOutHandler_optOut({
    onSuccess: async () => {
      // Invalidate related queries to refresh data
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('rentCreditReport');
        },
      });
      await options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Hook to opt in to payment reporting
 * Replaces: requestPaymentReportingOptIn (RSAA)
 *
 * @example
 * const optInMutation = usePaymentReportingOptIn({
 *   onSuccess: () => showNotification(PAYMENT_REPORTING_OPT_IN_SUCCESS),
 * });
 * await optInMutation.mutateAsync({ postData: optInPayload });
 */
export function usePaymentReportingOptIn(options?: {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: unknown) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();

  return useRentCreditReportOptInHandler_optIn({
    onSuccess: async () => {
      // Invalidate related queries to refresh data
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('rentCreditReport');
        },
      });
      await options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Hook to enroll renter in credit tracking
 * Replaces: requestEnrollRenterInCreditTracking (RSAA)
 *
 * @example
 * const enrollMutation = useEnrollRenterInCreditTracking({
 *   onSuccess: () => refetchCreditTrackingStatus(),
 * });
 * await enrollMutation.mutateAsync({ postData: { arrayUserId } });
 */
export function useEnrollRenterInCreditTracking(options?: {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: unknown) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();

  return useRentCreditReportEnrollRenterInCreditTrackingHandler_enrollRenterInCreditTracking({
    onSuccess: async () => {
      // Invalidate credit tracking queries to refresh data
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('rentCreditReport');
        },
      });
      await options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Hook to unenroll renter from credit tracking
 * Replaces: requestUnenrollRenterInCreditTracking (RSAA)
 *
 * @example
 * const unenrollMutation = useUnenrollRenterFromCreditTracking({
 *   onSuccess: () => refetchCreditTrackingStatus(),
 * });
 * await unenrollMutation.mutateAsync();
 */
export function useUnenrollRenterFromCreditTracking(options?: {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: unknown) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();

  return useRentCreditReportUnenrollRenterFromCreditTrackingHandler_unenrollRenterFromCreditTracking({
    onSuccess: async () => {
      // Invalidate credit tracking queries to refresh data
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes('rentCreditReport');
        },
      });
      await options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

// Re-export the request type for opt-in (consumers will need to build this payload)
export type { RentCreditReportOptInWebApiRequestDto };

// ============================================================================
// COMPOSITE HOOKS
// ============================================================================

type UsePaymentReportingDataParams = {
  featureFlagEnableRentReporting: boolean;
  enableCreditTracking: boolean;
  rentTermsKey?: string;
};

type PaymentReportingData = {
  optedIn: boolean;
  optInDate: string;
  creditTracking?: {
    arrayUserToken?: string;
    enrollmentStatus: string;
    enrollmentTimestamp?: number;
    lastFetchedTimestamp: number;
  };
};

type UsePaymentReportingDataReturn = {
  paymentReportingData: PaymentReportingData | undefined;
  paymentReportHistoryData: RentCreditReportWebApiPaymentReportHistoryResponseDto | undefined;
  isLoadingPaymentReporting: boolean;
  shouldShowPaymentReportingCard: boolean;
};

/**
 * Composite hook to fetch and transform payment reporting data
 * Handles conditional fetching based on credit tracking feature flag
 *
 * @example
 * const { paymentReportingData, isLoadingPaymentReporting } = usePaymentReportingData({
 *   featureFlagEnableRentReporting: true,
 *   enableCreditTracking: true,
 *   rentTermsKey: 'abc123',
 * });
 */
export function usePaymentReportingData({
  featureFlagEnableRentReporting,
  enableCreditTracking,
  rentTermsKey,
}: UsePaymentReportingDataParams): UsePaymentReportingDataReturn {
  const shouldFetchWithCreditTracking = Boolean(featureFlagEnableRentReporting && enableCreditTracking && rentTermsKey);

  const { data: paymentReportingStatusWithCreditTrackingResponse, isLoading: isLoadingPaymentReporting } =
    usePaymentReportingStatusWithCreditTracking(rentTermsKey, {
      enabled: shouldFetchWithCreditTracking,
    });

  const { data: paymentReportingHistoryResponse } = usePaymentReportingHistory(rentTermsKey, {
    enabled: shouldFetchWithCreditTracking,
  });

  let paymentReportingData: PaymentReportingData | undefined = undefined;

  if (paymentReportingStatusWithCreditTrackingResponse) {
    paymentReportingData = {
      optedIn: paymentReportingStatusWithCreditTrackingResponse.optInStatus,
      optInDate: paymentReportingStatusWithCreditTrackingResponse.optInDate,
      creditTracking: {
        ...paymentReportingStatusWithCreditTrackingResponse.creditTracking,
        lastFetchedTimestamp: Date.now(),
      },
    };
  }

  const shouldShowPaymentReportingCard =
    featureFlagEnableRentReporting &&
    (!shouldFetchWithCreditTracking || !isLoadingPaymentReporting) &&
    paymentReportingData?.optedIn !== false;

  return {
    paymentReportingData,
    paymentReportHistoryData: paymentReportingHistoryResponse,
    isLoadingPaymentReporting,
    shouldShowPaymentReportingCard,
  };
}
